import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  Plus, 
  Download, 
  Package2, 
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { bomHeaderApiService, BomHeader } from '../services/bomHeaderApi';
import { PermissionGate } from './ui/PermissionGate';

export function BOMListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [bomList, setBomList] = useState<BomHeader[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isInitialMount, setIsInitialMount] = useState(true);

  // BOM management route (from backend permissions)
  const BOM_ROUTE = '/bom';

  // Fetch BOM data from API with pagination
  const fetchBOMData = useCallback(async (page: number = currentPage, search: string = debouncedSearchTerm) => {
    try {
      setLoading(true);
      
      let response;
      
      // Use search API if there's a search term, otherwise use paginated API
      if (search.trim()) {
        response = await bomHeaderApiService.searchBomHeadersPaginated(search, page, pageSize);
      } else {
        try {
          response = await bomHeaderApiService.getBomHeadersPaginated(
            page,
            pageSize,
            undefined,
            undefined,
            undefined
          );
        } catch (paginationError) {
          const allBoms = await bomHeaderApiService.getBomHeaders();
          response = {
            data: allBoms,
            pagination: {
              totalCount: allBoms.length,
              totalPages: Math.ceil(allBoms.length / pageSize),
              currentPage: 1
            }
          };
        }
      }
      
      const boms = response.data || [];
      const pagination = response.pagination || {};
      const totalCount = pagination.totalCount ?? boms.length;
      const totalPages = pagination.totalPages ?? Math.ceil(totalCount / pageSize);
      
      setBomList(boms);
      setTotalCount(totalCount);
      setTotalPages(totalPages);
      
      console.log('BOM Pagination Debug:', {
        currentPage: page,
        pageSize,
        totalCount,
        totalPages,
        dataLength: boms.length,
        searchTerm: search
      });
    } catch (error) {
      toast.error('Failed to fetch BOM data');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchTerm]);

  // Load data on component mount
  useEffect(() => {
    fetchBOMData(1, '');
    setIsInitialMount(false);
  }, []);

  // Debounce search term for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch when search term (debounced) or page changes
  useEffect(() => {
    if (isInitialMount) return;
    setCurrentPage(1);
    fetchBOMData(1, debouncedSearchTerm);
  }, [debouncedSearchTerm, isInitialMount, fetchBOMData]);

  useEffect(() => {
    if (currentPage <= 1) return;
    fetchBOMData(currentPage, debouncedSearchTerm);
  }, [currentPage, debouncedSearchTerm, fetchBOMData]);

  const handleRefresh = () => {
    fetchBOMData(currentPage, debouncedSearchTerm);
  };

  const handleExport = () => {
    toast.success('BOM list exported successfully');
  };

  const handleView = (bomId: string) => {
    navigate(`/bom/view/${bomId}`);
  };

  // BOM can be edited but not deleted
  const handleEdit = (bomId: string) => {
    navigate(`/bom/edit/${bomId}`);
  };

  // BOM cannot be deleted
  // const handleDelete = async (bomId: string) => {
  //   try {
  //     await bomHeaderApiService.deleteBomHeader(bomId);
  //     toast.success('BOM deleted successfully');
  //     await fetchBOMData(currentPage, debouncedSearchTerm); // Refresh the list
  //   } catch (error) {
  //     toast.error('Failed to delete BOM');
  //   }
  // };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };


  return (
    <Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
      <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
              <Package2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle>Bill of Materials Listing</CardTitle>
              <CardDescription className="text-primary-100">
                Manage and view all BOMs for end goods manufacturing
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={handleRefresh}
              variant="secondary" 
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={handleExport}
              variant="secondary" 
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <PermissionGate route={BOM_ROUTE} action="create">
              <Button 
                size="sm"
                className="bg-white text-primary-600 hover:bg-white/90"
                onClick={() => navigate('/bom/new')}
              >
                <Plus className="h-4 w-4 mr-2" />
                New BOM
              </Button>
            </PermissionGate>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search BOMs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
            />
          </div>
          <div className="text-sm text-gray-600">
            {searchTerm ? `${totalCount} BOMs found` : `${totalCount} total BOMs`}
          </div>
        </div>

        {/* BOM Table */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
              <Package2 className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
              BOM Records
            </h3>
          </div>
          <div className="h-0.5 bg-gradient-to-r from-primary-200 to-primary-300"></div>
          
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-primary-50">
                <TableHead className="font-semibold text-primary-800">BOM Name</TableHead>
                <TableHead className="font-semibold text-primary-800">End Good</TableHead>
                <TableHead className="font-semibold text-primary-800">Category</TableHead>
                <TableHead className="font-semibold text-primary-800">Status</TableHead>
                <TableHead className="font-semibold text-primary-800">Process Weight</TableHead>
                <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bomList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    {loading
                      ? 'Loading BOMs...'
                      : searchTerm
                        ? `No BOMs found matching "${searchTerm}"`
                        : 'No BOMs found'}
                  </TableCell>
                </TableRow>
              ) : (
                bomList.map((bom) => (
                    <TableRow key={bom.id} className="hover:bg-primary-50/50">
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">{bom.bomName}</div>
                          <div className="text-xs text-gray-500">{bom.bomNumber}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">{bom.endgood?.endgoodName ?? bom.endgoodName ?? 'N/A'}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-primary-200 text-primary-700">
                          {bom.endgood?.endgoodCategory?.categoryName ?? bom.endgoodCategoryName ?? 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {bom.isActive ? (
                          <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-gray-300 text-gray-700 bg-gray-50">
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {bom.endgoodProcessWeightAvailable === true ? (
                          <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                            Not available
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <PermissionGate route={BOM_ROUTE} action="view">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(bom.id.toString())}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={BOM_ROUTE} action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(bom.id.toString())}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          {/* BOM cannot be deleted */}
                          {/* <PermissionGate route={BOM_ROUTE} action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(bom.id.toString())}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate> */}
                        </div>
                      </TableCell>
                    </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}